import React, { useCallback, useEffect, useRef } from 'react';
import ColorToolIcon from '../../colorTools/ColorToolIcon';
import { ToolbarItemConfig } from '../../../../toolbarTypes';
import { useDispatch, useSelector } from 'react-redux';
import { activePopupSelector } from '../../../../../../components/popupPanel/popupOpenSelector';
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';
import ToolbarPopupTool from '../../../ToolbarPopupTool';
import { getActiveToolbarLabel, getEditingLabelId } from '../../../../../../element/labels/labelSelectors';
import { getColorDisplayValue } from '../../../../../../../common/colors/elementColorFormatUtil';
import { clearTemporaryLabelColorStyles } from '../../colorTools/setTemporaryLabelColorStyles';
import { useLabelColorHandler } from '../hooks/useLabelColorHandler';
import { closePopup, togglePopup } from '../../../../../../components/popupPanel/popupActions';
import { stopDefaultAndPropagation } from '../../../../../../utils/domUtil';
import DesktopLabelColorPopup from './DesktopLabelColorPopup';
import CustomColorInput from '../../colorTools/CustomColorInput';

type LabelColorToolProps = {
    item: ToolbarItemConfig;
};

const LabelColorTool: React.FC<LabelColorToolProps> = (props: LabelColorToolProps) => {
    const { item } = props;

    const dispatch = useDispatch();
    const selectedLabel = useSelector(getActiveToolbarLabel);
    const editingLabelId = useSelector(getEditingLabelId);
    const activePopup = useSelector(activePopupSelector);

    const popupId = item?.uid || PopupIds.LABEL_TOOL_COLOR;
    const isPopupOpen = activePopup.has(popupId);
    const shouldOpenOnMouseDown = editingLabelId === selectedLabel?._id;
    const customColorInputRef = useRef<CustomColorInput>(null);

    const { handleTempColorChange, handleColorChange, tempColor, setTempColor, clearPreview } =
        useLabelColorHandler(selectedLabel);

    useEffect(() => {
        if (!selectedLabel) return;

        if (!isPopupOpen) {
            clearTemporaryLabelColorStyles(selectedLabel._id);
            setTempColor(null);
            return;
        }

        return () => {
            clearTemporaryLabelColorStyles(selectedLabel._id);
        };
    }, [isPopupOpen, selectedLabel?._id]);

    const openCustomColorPicker = useCallback(() => {
        customColorInputRef.current?.open();
        dispatch(closePopup(popupId, false));
    }, [dispatch, popupId]);

    if (!selectedLabel) return null;

    const labelColor = tempColor ?? selectedLabel.color;
    const labelColorDisplayValue = getColorDisplayValue(labelColor);

    const popupProps = {
        selectedLabel,
        tempColor,
        customColorInputRef,
        handleTempColorChange,
        handleColorChange,
        clearPreview,
        openCustomColorPicker,
    };

    const handleOnClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!shouldOpenOnMouseDown) return;

        stopDefaultAndPropagation(event);
    };

    const handleMouseDownCapture = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!shouldOpenOnMouseDown) return;
        if (event.button !== 0 || event.defaultPrevented) return;

        dispatch(togglePopup({ popupId }));
    };

    return (
        <div onMouseDownCapture={handleMouseDownCapture} onClickCapture={handleOnClick}>
            <ToolbarPopupTool
                {...props}
                {...popupProps}
                toolName="label-color"
                popupId={popupId}
                buttonSelector=".Label-colorTool .ColorToolIcon"
                IconComponent={() => (
                    <ColorToolIcon
                        key={`label-color-${selectedLabel._id}`}
                        supportColorBar={false}
                        supportBackgroundColor={false}
                        selectedType={undefined}
                        color={labelColorDisplayValue}
                        isActive={isPopupOpen}
                    />
                )}
                PopupComponent={DesktopLabelColorPopup}
            />
        </div>
    );
};

export default LabelColorTool;
